/* eslint-disable react/prop-types */
/** @jsx createElementEntity */
import { Layers } from "../camera-layers";
import { cloneModelFromCache, loadModel } from "../components/gltf-model-plus";
import { COLLISION_LAYERS } from "../constants";
import { Fit, Shape } from "../inflators/physics-shape";
import { createElementEntity, createRef } from "../utils/jsx-entity";
import { preload } from "../utils/preload";
import { BUTTON_TYPES } from "./button3D";

import { textureLoader } from "../utils/media-utils";

import buttonSrc from "../assets/hud/camera-screen.png";

const buttonTexture = textureLoader.load(buttonSrc);

import { Button3DCamera } from "./button3DCamera";

import snapOverlaySrc from "../assets/hud/snap-menu-overlay.png";

const snapOverlayTexture = textureLoader.load(snapOverlaySrc);

const cameraModelSrc = require("../assets/camera_tool_app.glb");

import photoSrc from "../assets/hud/icon_photo.png";
import videoSrc from "../assets/hud/icon_video.png";
import cameraSnapSrc from "../assets/hud/camera_snap.png";
import arrowBackSrc from "../assets/hud/arrow_back_icon.png";
import screenRecordSrc from "../assets/hud/screen_record_video.png";
import screenRecordStopSrc from "../assets/hud/screen_record_stop.png";
import swapCameraTextureSrc from "../assets/hud/swap_camera.png";
import { getThemeColor } from "../utils/theme";

const photoTexture = textureLoader.load(photoSrc);

const videoTexture = textureLoader.load(videoSrc);

const cameraTexture = textureLoader.load(cameraSnapSrc);

const arrowBackTexture = textureLoader.load(arrowBackSrc);

const screenRecordTexture = textureLoader.load(screenRecordSrc);

const screenRecordStopTexture = textureLoader.load(screenRecordStopSrc);

const swapCameraTexture = textureLoader.load(swapCameraTextureSrc);

export function Label({ text = {}, ...props }, ...children) {
  const value = children.join("\n");
  const textObj = {
    value,
    ...text
  };

  return <entity name="Label" text={textObj} layers={1 << Layers.CAMERA_LAYER_UI} {...props} />;
}

export function CountdownLabel({ text = {}, ...props }, ...children) {
  const value = children.join("\n");
  const textObj = {
    value,
    color: getThemeColor("primary-color"),
    outlineWidth: 0.0025,
    outlineColor: getThemeColor("primary-color"),
    ...text
  };

  return <entity name="Label" text={textObj} layers={1 << Layers.CAMERA_LAYER_UI} {...props} />;
}

const RENDER_WIDTH = 1280;
const RENDER_HEIGHT = 720;

// We intentionally do not remove this model from the GLTF Cache
preload(loadModel(cameraModelSrc, null, true));

export function CameraPrefab() {
  const snapMenuRef = createRef();
  const nextButtonRef = createRef();
  const prevButtonRef = createRef();
  const snapRef = createRef();
  const snapModeRef = createRef();
  const cancelRef = createRef();
  const recVideoRef = createRef();
  const recModeVideoRef = createRef();
  const swapCameraRef = createRef();
  const recVideoStopRef = createRef();
  const screenRef = createRef();
  const selfieScreenRef = createRef();
  const cameraRef = createRef();
  const backCameraRef = createRef();
  const countdownLblRef = createRef();
  const captureDurLblRef = createRef();
  const sndToggleRef = createRef();

  const width = 0.34;
  const aspect = 1280 / 720;
  const screenGeometry = new THREE.PlaneGeometry(width, width / aspect);

  const camera = new THREE.PerspectiveCamera(50, RENDER_WIDTH / RENDER_HEIGHT, 0.1, 30000);
  camera.layers.enable(Layers.CAMERA_LAYER_VIDEO_TEXTURE_TARGET);
  camera.layers.enable(Layers.CAMERA_LAYER_THIRD_PERSON_ONLY);

  const backCamera = new THREE.PerspectiveCamera(50, RENDER_WIDTH / RENDER_HEIGHT, 0.1, 30000);
  backCamera.layers.enable(Layers.CAMERA_LAYER_VIDEO_TEXTURE_TARGET);
  backCamera.layers.enable(Layers.CAMERA_LAYER_THIRD_PERSON_ONLY);

  backCamera.position.set(0, 0, -0.1); // Place back camera behind the avatar, looking outward
  backCamera.rotation.y = Math.PI; // Rotate to face outward from avatar

  // Placeholder texture to avoid errors before renderTarget is set
  const placeholderTexture = new THREE.Texture();
  placeholderTexture.needsUpdate = true;

  const selfieScreenMaterial = new THREE.MeshBasicMaterial({
    map: buttonTexture, // Selfie camera render texture
    alphaMap: buttonTexture, // Rounded corners mask
    transparent: true,
    toneMapped: false, // Match the screenMaterial behavior
    side: THREE.DoubleSide
  });

  //selfie screen material

  const uiZ = 0.1;

  const buttonPhoto = new THREE.Mesh(
    new THREE.PlaneGeometry(0.055, 0.055),
    new THREE.MeshBasicMaterial({ map: photoTexture, transparent: true })
  );
  buttonPhoto.renderOrder = 1;

  const videoIconObject = new THREE.Mesh(
    new THREE.PlaneGeometry(0.055, 0.055),
    new THREE.MeshBasicMaterial({ map: videoTexture, transparent: true })
  );
  videoIconObject.renderOrder = 2;

  const photoCaptureObj = new THREE.Mesh(
    new THREE.PlaneGeometry(0.06, 0.06),
    new THREE.MeshBasicMaterial({ map: cameraTexture, toneMapped: false, transparent: true })
  );
  photoCaptureObj.renderOrder = 3;

  const arrowBackObj = new THREE.Mesh(
    new THREE.PlaneGeometry(0.035, 0.035),
    new THREE.MeshBasicMaterial({ map: arrowBackTexture, toneMapped: false, transparent: true })
  );
  arrowBackObj.renderOrder = 4;

  const screenRecordObj = new THREE.Mesh(
    new THREE.PlaneGeometry(0.055, 0.055),
    new THREE.MeshBasicMaterial({ map: screenRecordTexture, toneMapped: false, transparent: true })
  );
  screenRecordObj.renderOrder = 5;

  const screenRecordObjStop = new THREE.Mesh(
    new THREE.PlaneGeometry(0.055, 0.055),
    new THREE.MeshBasicMaterial({ map: screenRecordStopTexture, toneMapped: false, transparent: true })
  );
  screenRecordObjStop.renderOrder = 6;

  const swapCameraObj = new THREE.Mesh(
    new THREE.PlaneGeometry(0.05, 0.05),
    new THREE.MeshBasicMaterial({ map: swapCameraTexture, toneMapped: false, transparent: true })
  );
  swapCameraObj.renderOrder = 7;

  return (
    <entity
      name="Camera Tool"
      networked
      networkedTransform
      cursorRaycastable
      remoteHoverTarget
      handCollisionTarget
      offersRemoteConstraint
      offersHandConstraint
      makeKinematicOnRelease
      holdable
      floatyObject
      rigidbody={{ collisionGroup: COLLISION_LAYERS.INTERACTABLES, collisionMask: COLLISION_LAYERS.HANDS }}
      physicsShape={{ halfExtents: [0.22, 0.14, 0.1] }}
      cameraTool={{
        snapMenuRef,
        nextButtonRef,
        snapModeRef,
        prevButtonRef,
        snapRef,
        cancelRef,
        recVideoRef,
        recVideoStopRef,
        recModeVideoRef,
        screenRef,
        selfieScreenRef,
        cameraRef,
        countdownLblRef,
        captureDurLblRef,
        sndToggleRef,
        swapCameraRef,
        backCameraRef // New field for back camera reference
      }}
    >
      <entity
        name="Selfie Screen"
        ref={selfieScreenRef}
        position={[0, 0, 0.025]}
        scale={[-1.96, 1.75, 1.75]}
        object3D={new THREE.Mesh(screenGeometry, selfieScreenMaterial)}
        visible={false}
      />

      <entity name="Camera Model" model={{ model: cloneModelFromCache(cameraModelSrc).scene }} scale={[2, 2, 2]} />

      <entity ref={cameraRef} object3D={camera} position={[0, 0, 0.05]} rotation={[0, Math.PI, 0]} />

      <entity name="Snap Menu" ref={snapMenuRef} position={[0.0, 0, 0.03]}>
        <CountdownLabel ref={countdownLblRef} position={[0, 0, uiZ + 0.002]} />
        <Button3DCamera
          ref={snapRef}
          position={[0.29, 0, 0.002]}
          object3D={photoCaptureObj}
          type={BUTTON_TYPES.ACTION_CAMERA}
        />

        <Button3DCamera
          ref={snapModeRef}
          position={[0.29, 0.1, 0.002]}
          object3D={buttonPhoto}
          text={"Photo"}
          type={BUTTON_TYPES.ACTION_CAMERA}
        />

        <Button3DCamera
          ref={recModeVideoRef}
          position={[0.29, 0, 0.002]}
          object3D={videoIconObject}
          type={BUTTON_TYPES.ACTION_CAMERA}
          text={"Video"}
        />

        <Button3DCamera
          ref={recVideoRef}
          position={[0.29, 0, 0.002]}
          object3D={screenRecordObj}
          type={BUTTON_TYPES.ACTION_CAMERA}
        />
        <Button3DCamera
          ref={swapCameraRef}
          position={[0.29, 0.1, 0.002]}
          object3D={swapCameraObj}
          type={BUTTON_TYPES.ACTION_CAMERA}
        />

        <Button3DCamera
          ref={recVideoStopRef}
          position={[0.29, 0, 0.002]}
          object3D={screenRecordObjStop}
          type={BUTTON_TYPES.ACTION_CAMERA}
        />

        <Button3DCamera
          ref={cancelRef}
          position={[0.29, -0.11, 0.002]}
          object3D={arrowBackObj}
          type={BUTTON_TYPES.ACTION_CAMERA}
        />

        <entity
          ref={captureDurLblRef}
          layers={1 << Layers.CAMERA_LAYER_UI}
          text={{
            value: "00:00",
            color: getThemeColor("text-primary"),
            textAlign: "center",
            anchorX: "center",
            anchorY: "middle",
            fontSize: 0.02
          }}
          position={[0, 0.15, 0.002]}
          name={`${"capture"} Label`}
        />
      </entity>
    </entity>
  );
}

export function CubeMediaFramePrefab() {
  return (
    <entity
      name="Media Frame"
      networked
      networkedTransform
      cursorRaycastable
      remoteHoverTarget
      handCollisionTarget
      offersRemoteConstraint
      offersHandConstraint
      floatyObject
      destroyAtExtremeDistance
      holdable
      rigidbody={{
        gravity: -9.8,
        collisionGroup: COLLISION_LAYERS.INTERACTABLES,
        collisionMask:
          COLLISION_LAYERS.HANDS |
          COLLISION_LAYERS.ENVIRONMENT |
          COLLISION_LAYERS.INTERACTABLES |
          COLLISION_LAYERS.AVATAR
      }}
      physicsShape={{ fit: Fit.MANUAL, type: Shape.BOX, halfExtents: [0.5, 0.5, 0.5] }}
      object3D={new THREE.Mesh(new THREE.BoxGeometry(), new THREE.MeshStandardMaterial())}
    >
      <entity mediaFrame position={[0, 1, 0]} />
    </entity>
  );
}
